import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {
  ChatMessage,
  DataObjectId,
  GroupChat,
  newsCompanyId
} from 'yougile-model'
import {EventBus} from '@client/event-bus'
import {openChatEvent} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {MessageBox} from '@components/message-box'
import {getChats} from '@components/right-panel/task-chat-list/get-chats'
import {createUseStore} from '@containers/hooks'
import {ChatFilterState} from '@extensions/app-persistence'
import {ChatUtils} from '@extensions/chat-utils'
import {Feedback} from '@extensions/feedback'
import {globals} from '@extensions/globals-base'
import {ChatProxy} from '@extensions/proxies/chat-proxy'
import {getHubWithNewChat} from './utils/get-hub-with-new-chat'

interface State {
  message?: ChatMessage
  query: string
  chats: ChatProxy[]
  originalChatId?: DataObjectId
}

const chatFilter: ChatFilterState = {
  personal: true,
  myTasks: true,
  otherTasks: true,
  groupChats: true,
  starredOnTop: false,
  unreadOnTop: false,
  privateTasks: false
}

export class MessageForwardingStore {
  state: State = {
    message: undefined,
    query: '',
    chats: [],
    originalChatId: undefined
  }

  constructor(message: ChatMessage) {
    makeAutoObservable(this, {
      state: observable.shallow
    })
    this.state.message = message
    this.state.originalChatId = ChatUtils.getChatFromCurrentUrl()?.id
  }

  setChats(): void {
    const box = Sync.getCurrentSandbox()
    this.state.chats = getChats(box, chatFilter)
  }

  inputQuery(text: string): void {
    this.state.query = text.trim()
  }

  itemClick(
    chatId: DataObjectId,
    onSelect: (message: ChatMessage | undefined) => void
  ): void {
    const idChat = chatId.split(',')[0] as DataObjectId

    const {hub, box} = getHubWithNewChat(idChat)
    if (!hub) {
      return
    }
    const isGroupChat = GroupChat.isGroupChat(hub)
    const isNews = hub.companyId === newsCompanyId
    const isDirect =
      box.companyId === Sync.profileId &&
      Sync.getProfile().getProjectFor(hub)?.title === 'Direct'
    const isFeedbackTicket =
      !!Feedback.column && hub.location === Feedback.column.id
    const isTaskChat = !isGroupChat && !isNews && !isDirect && !isFeedbackTicket
    const canSendMessages = PermissionsChecker.checkSendMessages(hub)
    if (isTaskChat && !canSendMessages) {
      MessageBox.show(
        Lang.msg.messageForwarding.checkSendMessagesWarning,
        'warning'
      )
      return
    }
    hub.chat.addMessage(
      globals.currentUserId,
      {
        text: this.state.message?.text,
        textHtml: this.state.message?.getTextHtml()
      },
      {
        timestamp: Time.now(),
        properties: {
          originalChatId: this.state.originalChatId,
          originalMessageTime: this.state.message.timestamp,
          originalAuthorId: this.state.message.from,
          forwardingAuthorId: globals.currentUserId
        }
      }
    )
    hub.commit()
    EventBus.dispatch(openChatEvent(idChat))
    onSelect(undefined)
  }
}

const [MessageForwardingContext, useMessageForwardingStore] = createUseStore(
  MessageForwardingStore
)
export {MessageForwardingContext, useMessageForwardingStore}
